import React, { useContext, useEffect, useRef, useCallback } from "react";
import ROSLIB from "roslib";
import { ROSContext, ROSProvider, ConnectionState } from "./ROSContext";
import PropTypes from "prop-types";

// Backoff configuration
const BACKOFF_INITIAL_MS = 500;
const BACKOFF_MAX_MS = 30000;
const BACKOFF_MULTIPLIER = 2;

function useROS() {
  const [ros, setROS] = useContext(ROSContext);
  const reconnectTimeout = useRef(null);
  const backoffDelay = useRef(BACKOFF_INITIAL_MS);
  const wasConnected = useRef(false);
  // Timeout that fires if a connect attempt neither succeeds nor errors
  // (e.g. unreachable host hangs at CONNECTING) — without this, the UI
  // gets stuck on "RECONNECTING" forever.
  const connectAttemptTimeout = useRef(null);
  const CONNECT_ATTEMPT_TIMEOUT_MS = 5000;

  // Reset backoff to initial value
  const resetBackoff = useCallback(() => {
    backoffDelay.current = BACKOFF_INITIAL_MS;
  }, []);

  // Advance backoff with exponential increase up to max
  const nextBackoff = useCallback(() => {
    const current = backoffDelay.current;
    backoffDelay.current = Math.min(current * BACKOFF_MULTIPLIER, BACKOFF_MAX_MS);
    return current;
  }, []);

  const handleConnect = useCallback(() => {
    // Prevent duplicate connection attempts. We check the real WebSocket
    // readyState rather than ros.ROS.isConnected because on mobile the
    // OS can kill the socket silently, leaving the roslibjs flag set to
    // true while the underlying socket is dead. Only bail if we have a
    // live OPEN or CONNECTING socket; otherwise proceed to open one.
    // WebSocket.OPEN === 1, WebSocket.CONNECTING === 0.
    const existingSock = ros.ROS && ros.ROS.socket;
    if (existingSock && (existingSock.readyState === 0 || existingSock.readyState === 1)) {
      return;
    }

    const isReconnect = wasConnected.current;

    try {
      setROS((prev) => ({
        ...prev,
        connectionState: isReconnect ? ConnectionState.RECONNECTING : ConnectionState.CONNECTING,
      }));

      // Set up event listeners only once
      if (!ros.ROS._connectionEventSet) {
        ros.ROS.on("connection", () => {
          wasConnected.current = true;
          resetBackoff();
          // Clear pending reconnect and attempt timeout
          if (reconnectTimeout.current) {
            clearTimeout(reconnectTimeout.current);
            reconnectTimeout.current = null;
          }
          if (connectAttemptTimeout.current) {
            clearTimeout(connectAttemptTimeout.current);
            connectAttemptTimeout.current = null;
          }
          setROS((prev) => ({
            ...prev,
            isConnected: true,
            connectionState: ConnectionState.CONNECTED,
            ROSConfirmedConnected: false,
          }));
          getTopics();
          getServices();
        });

        ros.ROS.on("error", (error) => {
          // Don't log noisy WebSocket errors during reconnection attempts
        });

        ros.ROS.on("close", () => {
          if (connectAttemptTimeout.current) {
            clearTimeout(connectAttemptTimeout.current);
            connectAttemptTimeout.current = null;
          }
          setROS((prev) => ({
            ...prev,
            isConnected: false,
            connectionState: wasConnected.current ? ConnectionState.RECONNECTING : ConnectionState.DISCONNECTED,
            ROSConfirmedConnected: false,
          }));
          // Schedule reconnect with backoff
          scheduleReconnect();
        });

        ros.ROS._connectionEventSet = true;
      }

      // Guard: if the connect attempt hangs (e.g. host unreachable, DNS
      // hang, captive portal), force a teardown + retry so the UI doesn't
      // get stuck in "RECONNECTING" forever.
      if (connectAttemptTimeout.current) {
        clearTimeout(connectAttemptTimeout.current);
      }
      connectAttemptTimeout.current = setTimeout(() => {
        connectAttemptTimeout.current = null;
        const sock = ros.ROS && ros.ROS.socket;
        // If still not OPEN, kill the half-open socket and schedule a retry.
        if (!sock || sock.readyState !== 1) {
          try { ros.ROS.close(); } catch (_) {}
          try {
            if (ros.ROS) {
              ros.ROS.socket = null;
              ros.ROS.isConnected = false;
            }
          } catch (_) {}
          scheduleReconnect();
        }
      }, CONNECT_ATTEMPT_TIMEOUT_MS);

      ros.ROS.connect(ros.url);
    } catch (e) {
      console.error("Connection exception:", e);
      scheduleReconnect();
    }
  }, [ros.ROS, ros.url]);

  // Schedule a reconnect attempt with exponential backoff
  const scheduleReconnect = useCallback(() => {
    // Don't schedule if autoconnect is off or already scheduled
    if (reconnectTimeout.current) return;

    const delay = nextBackoff();
    reconnectTimeout.current = setTimeout(() => {
      reconnectTimeout.current = null;
      if (!ros.ROS.isConnected && ros.autoconnect) {
        handleConnect();
      }
    }, delay);
  }, [ros.ROS, ros.autoconnect, handleConnect, nextBackoff]);

  // Force-close any existing socket (even zombie ones) and reconnect.
  // Use this on visibility/online events because on mobile the OS may
  // kill the WebSocket without firing a `close` event — roslibjs's
  // internal `isConnected` flag can then read true while the socket
  // is actually dead. Closing forces the `close` handler to fire,
  // which resets state, and then we reconnect.
  const forceReconnect = useCallback(() => {
    if (!ros.autoconnect) return;
    try {
      ros.ROS.close();
    } catch (_) {
      // ignore — socket may already be closed
    }
    // Wipe the dead/zombie socket reference and the roslibjs internal
    // flag so handleConnect() doesn't think we're still connected and
    // skip opening a new socket. Mobile OS-killed sockets leave both
    // stale.
    try {
      if (ros.ROS) {
        ros.ROS.socket = null;
        ros.ROS.isConnected = false;
      }
    } catch (_) {
      // defensive — roslib internals
    }
    resetBackoff();
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    // Defer one tick so any pending close handler can fire first.
    setTimeout(() => {
      handleConnect();
    }, 0);
  }, [ros.ROS, ros.autoconnect, handleConnect, resetBackoff]);

  // Initial connection + cleanup
  useEffect(() => {
    if (!ros.isConnected && ros.autoconnect) {
      handleConnect();
    }

    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
    };
    // ros.url included so changing the IP in the dropdown kicks off a
    // fresh connect attempt at the new host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ros.autoconnect, ros.url]);

  // Network event handlers.
  //
  // NOTE: we deliberately do NOT force a reconnect on `visibilitychange`
  // (returning to the tab/app). That handler used to call forceReconnect(),
  // which unconditionally closes the socket and reopens it every time the
  // page regained focus — so just switching back to the app caused a visible
  // disconnect/reconnect cycle even when the existing connection was perfectly
  // healthy. The heartbeat below already detects a *genuinely* dead socket
  // (within HEARTBEAT_INTERVAL_MS of the timers resuming on foreground) and
  // reconnects only then, so foreground recovery is still covered without
  // tearing down good connections.
  useEffect(() => {
    const handleOnline = () => {
      // Network came back — force a clean reconnect.
      forceReconnect();
    };

    const handleOffline = () => {
      setROS((prev) => ({
        ...prev,
        isConnected: false,
        connectionState: ConnectionState.RECONNECTING,
      }));
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [forceReconnect]);

  // Heartbeat: while we believe we're connected, periodically verify the
  // underlying WebSocket is actually OPEN. Mobile browsers can let the
  // socket die silently without firing a `close` event; this catches that
  // and triggers a reconnect.
  useEffect(() => {
    if (!ros.isConnected || !ros.autoconnect) return;

    const HEARTBEAT_INTERVAL_MS = 5000;
    const interval = setInterval(() => {
      const sock = ros.ROS && ros.ROS.socket;
      // WebSocket.OPEN === 1; treat anything else as dead.
      if (!sock || sock.readyState !== 1) {
        forceReconnect();
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [ros.isConnected, ros.autoconnect, ros.ROS, forceReconnect]);

  function toggleConnection() {
    if (ros.isConnected) {
      handleDisconnect();
    } else {
      resetBackoff();
      wasConnected.current = false;
      // Re-enable autoconnect — user explicitly asked to connect, so
      // future background→foreground transitions should reconnect too.
      setROS((prev) => ({ ...prev, autoconnect: true }));
      handleConnect();
    }
  }

  function toggleAutoconnect() {
    setROS((prev) => ({ ...prev, autoconnect: !prev.autoconnect }));
  }

  function changeUrl(new_url) {
    setROS((prev) => ({ ...prev, url: new_url }));
  }

  function getTopics() {
    const topicsPromise = new Promise((resolve, reject) => {
      ros.ROS.getTopics(
        (topics) => {
          const topicList = topics.topics.map((topicName, i) => {
            return {
              path: topicName,
              msgType: topics.types[i],
              type: "topic",
            };
          });
          resolve({ topics: topicList });
        },
        (message) => {
          console.error("Failed to get topic", message);
          reject(message);
        }
      );
    });
    topicsPromise.then((topics) =>
      setROS((prev) => ({ ...prev, topics: topics.topics }))
    ).catch(() => {});
    return ros.topics;
  }

  function getServices() {
    const servicesPromise = new Promise((resolve, reject) => {
      ros.ROS.getServices(
        (services) => {
          const serviceList = services.map((serviceName) => {
            return {
              path: serviceName,
              type: "service",
            };
          });
          resolve({ services: serviceList });
        },
        (message) => {
          console.error("Failed to get services", message);
          reject(message);
        }
      );
    });
    servicesPromise.then((services) =>
      setROS((prev) => ({ ...prev, services: services.services }))
    ).catch(() => {});
    return ros.services;
  }

  function createListener(topic, msg_type, to_queue, compression_type, throttle_rate) {
    var newListener = new ROSLIB.Topic({
      ros: ros.ROS,
      name: topic,
      messageType: msg_type,
      queue_length: to_queue,
      compression: compression_type,
      throttle_rate: throttle_rate,
    });

    for (var listener in ros.listeners) {
      if (newListener.name === ros.listeners[listener].name) {
        ros.listeners[listener].throttle_rate = throttle_rate;
        return ros.listeners[listener];
      }
    }
    ros.listeners.push(newListener);
    return newListener;
  }

  function createPublisher(topic, msg_type) {
    var newPublisher = new ROSLIB.Topic({
      ros: ros.ROS,
      name: topic,
      messageType: msg_type,
    });

    for (var publisher in ros.publishers) {
      if (newPublisher.name === ros.publishers[publisher].name) {
        return ros.publishers[publisher];
      }
    }
    ros.publishers.push(newPublisher);
    return newPublisher;
  }

  function createService(service, srv_type) {
    if (!ros.services) ros.services = [];
    var newService = new ROSLIB.Service({
      ros: ros.ROS,
      name: service,
      serviceType: srv_type,
    });
    for (var svc in ros.services) {
      if (newService.name === ros.services[svc].name) {
        return ros.services[svc];
      }
    }
    ros.services.push(newService);
    return newService;
  }

  function createSrv(srv) {
    var newService = new ROSLIB.ServiceRequest(srv);
    return newService;
  }

  function createMessage(msg) {
    var newMessage = new ROSLIB.Message(msg);
    return newMessage;
  }

  function setParam(param, value) {
    var newParam = new ROSLIB.Param({
      ros: ros.ROS,
      name: param,
    });

    return new Promise((resolve, reject) => {
      newParam.set(value, (response) => {
        resolve(response);
      });
    });
  }

  const handleDisconnect = () => {
    try {
      // Cancel any pending reconnect or pending connect-attempt timeout
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      if (connectAttemptTimeout.current) {
        clearTimeout(connectAttemptTimeout.current);
        connectAttemptTimeout.current = null;
      }
      resetBackoff();
      wasConnected.current = false;
      ros.ROS.close();
      setROS((prev) => ({
        ...prev,
        isConnected: false,
        connectionState: ConnectionState.DISCONNECTED,
        topics: [],
        listeners: [],
        ROSConfirmedConnected: false,
        // Honor explicit user disconnect — don't auto-reconnect on
        // next visibility change. toggleConnection() re-enables this.
        autoconnect: false,
      }));
    } catch (e) {
      // silent
    }
  };

  const removeAllListeners = () => {
    for (var mlistener in ros.listeners) {
      ros.listeners[mlistener].removeAllListeners();
    }
    setROS((prev) => ({ ...prev, listeners: [] }));
  };

  function removeListener(listener) {
    for (var mlistener in ros.listeners) {
      if (listener.name === ros.listeners[mlistener].name) {
        ros.listeners.splice(mlistener, 1);
        listener.removeAllListeners();
        return;
      }
    }
  }

  return {
    toggleConnection,
    changeUrl,
    getTopics,
    getServices,
    createListener,
    createPublisher,
    createMessage,
    createService,
    createSrv,
    toggleAutoconnect,
    removeAllListeners,
    removeListener,
    checkConnection: handleConnect,
    setParam,
    ros: ros.ROS,
    isConnected: ros.isConnected,
    connectionState: ros.connectionState,
    autoconnect: ros.autoconnect,
    url: ros.url,
    topics: ros.topics,
    services: ros.services,
    listeners: ros.listeners,
  };
}

function ROS(props) {
  return <ROSProvider>{props.children}</ROSProvider>;
}

ROS.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useROS, ROS };
